import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { TestGroup } from '../models/test-group.modal';
import { TestGroupService } from 'src/app/services/test-group.service';
import { inject, Injectable } from '@angular/core';
import { EMPTY, map, Observable } from 'rxjs';
import { CurrentAccountRestored, SetCurrentAccountLocation } from 'src/states/current-account/current-account.actions';
import { FetchFavoriteTestGroups } from 'src/app/pages/orders/modules/order/state/actions/test-group.actions';
import { CurrentAccountState } from 'src/states';
import { Logout } from 'src/modules/security/actions/auth-actions';
import { LabSelectionChanged } from '../components/order-info/states/actions/order-info-step.actions';
import { LabSelectionByFeatureChanged } from 'src/app/states/actions/lab-selection.actions';
import { sortByProperty } from 'src/app/utils/sort-by-property.util';

interface TestGroupsStateModel {
  status: 'notLoaded' | 'loading' | 'loaded';
  practiceId: number | null;
  testGroups: TestGroup[];
}

const defaults: TestGroupsStateModel = {
  status: 'notLoaded',
  practiceId: null,
  testGroups: []
};

export const TEST_GROUPS_STATE_TOKEN = new StateToken<TestGroupsStateModel>('testGroups');

@State<TestGroupsStateModel>({
  name: TEST_GROUPS_STATE_TOKEN,
  defaults
})
@Injectable()
export class TestGroupsState {
  private readonly store: Store = inject(Store);
  private readonly testGroupService: TestGroupService = inject(TestGroupService);

  @Selector()
  public static loaded(state: TestGroupsStateModel): boolean {
    return state.status === 'loaded';
  }

  @Selector()
  public static testGroups(state: TestGroupsStateModel): TestGroup[] {
    return sortByProperty(state.testGroups, 'name');
  }

  @Action(FetchFavoriteTestGroups)
  public fetchTestGroups(ctx: StateContext<TestGroupsStateModel>, { laboratoryFacilityId }: FetchFavoriteTestGroups): Observable<void> {
    const practiceId = this.store.selectSnapshot(CurrentAccountState.practiceId);

    return this.fetchFavoriteTestGroups(practiceId, laboratoryFacilityId, ctx);
  }

  private fetchFavoriteTestGroups(
    practiceId: number | undefined,
    laboratoryFacilityId: number,
    ctx: StateContext<TestGroupsStateModel>
  ): Observable<void> {
    if (!practiceId) {
      console.error('practiceId is empty');
      return EMPTY;
    }

    ctx.patchState({ practiceId: practiceId, status: 'loading' });

    return this.testGroupService
      .getFavoriteTestGroups(practiceId, laboratoryFacilityId)
      .pipe(map(result => ctx.patchState({ status: 'loaded', testGroups: result })));
  }

  @Action([CurrentAccountRestored, SetCurrentAccountLocation, Logout, LabSelectionChanged, LabSelectionByFeatureChanged])
  public clearState(ctx: StateContext<TestGroupsStateModel>): void {
    ctx.setState(defaults);
  }
}
